<template>
	<div class="form-box">
		<template v-for="item in fields">
			<!-- 留言内容 -->
			<template v-if="item.fieldType === 0 && Number(item.systemType) === 7">
				<div class="item item--textarea" :key="item.uuid">
					<LabelField :item="item" />
					<div class="item-content item-content--textarea">
						<van-field
							v-model="item.dynamicFieldValue"
							type="textarea"
							:placeholder="item.dynamicFieldDescribed"
							:maxlength="1000"
							show-word-limit
							rows="3"
							class="textarea-field"
						/>
					</div>
				</div>
			</template>

			<!-- 姓名 -->
			<template v-if="item.fieldType === 0 && Number(item.systemType) === 1">
				<div class="item" :key="item.uuid">
					<LabelField :item="item" />
					<input
						class="common-input"
						v-model="item.dynamicFieldValue"
						type="text"
						:placeholder="item.dynamicFieldDescribed"
					/>
				</div>
			</template>

			<!-- 联系方式 -->
			<template v-if="item.fieldType === 0 && Number(item.systemType) === 2">
				<div class="item" :key="item.uuid">
					<LabelField :item="item" />
					<div class="common-flex">
						<div class="area-input" @click="openAreaCodePicker">
							<span class="area-code">+{{ innerAreaCode }}</span>
							<i class="icon-font drop-popup">&#xe68e;</i>
							<span class="area-separator"></span>
						</div>
						<van-field
							v-model="item.dynamicFieldValue"
							:placeholder="item.dynamicFieldDescribed"
							maxlength="11"
							type="tel"
							@input="validateNumber($event, item)"
							class="clear-van-field"
						/>
					</div>
				</div>
			</template>

			<!-- 机构 -->
			<template v-if="item.fieldType === 0 && Number(item.systemType) === 3">
				<div class="item" :key="item.uuid">
					<cSearchPicker
						externalClass="c-search"
						:externalValue="item.dynamicFieldValue"
						inputType="textarea"
						:placeholder="item.dynamicFieldDescribed"
						@select="inputCompany($event, item)"
						@input="inputCompany($event, item)"
					>
						<LabelField :item="item" />
					</cSearchPicker>
				</div>
			</template>

			<!-- 职位 -->
			<template v-if="item.fieldType === 0 && Number(item.systemType) === 4">
				<div class="item" :key="item.uuid">
					<LabelField :item="item" />
					<input
						class="common-input"
						v-model="item.dynamicFieldValue"
						type="text"
						:placeholder="item.dynamicFieldDescribed"
					/>
				</div>
			</template>

			<!-- 邮箱 -->
			<template v-if="item.fieldType === 0 && Number(item.systemType) === 5">
				<div class="item" :key="item.uuid">
					<LabelField :item="item" />
					<input
						class="common-input"
						v-model="item.dynamicFieldValue"
						type="text"
						:placeholder="item.dynamicFieldDescribed"
					/>
				</div>
			</template>
			<!-- 邮箱 & 验证码 -->
			<template v-if="item.fieldType === 0 && Number(item.systemType) === 8">
				<div class="item" :key="`${item.uuid}-email`">
					<LabelField :item="item" />
					<input
						class="common-input"
						v-model="item.dynamicFieldValue"
						type="text"
						:placeholder="item.dynamicFieldDescribed"
					/>
				</div>
				<div class="item" :key="`${item.uuid}-auth`">
					<span class="label email-auth-label"
						>{{ proxy.$t('enterprise.normal-field.verification')
						}}<span v-if="item.config === 1" class="required ml6">*</span></span
					>
					<div class="common-flex">
						<input
							class="common-input email-auth-input"
							v-model="innerAuthCode"
							type="text"
							:placeholder="verificationPlaceholder"
							@input="emitAuthCode"
						/>
						<div
							class="item-suffix"
							:class="{ 'item-suffix--disabled': countdown > 0 }"
							@click="handleSendVerifyCode(item)"
						>
							<span v-if="countdown === 0">{{ proxy.$t('enterprise.normal-field.send-code') }}</span>
							<span v-else>{{ countdown }}S</span>
						</div>
					</div>
				</div>
			</template>

			<!-- 名片 -->
			<template v-if="item.fieldType === 0 && Number(item.systemType) === 6">
				<div class="upload-box item" :key="item.uuid">
					<div style="flex: 1">
						<LabelField :item="item" />
						<div class="upload-tip">{{ item.dynamicFieldDescribed }}</div>
					</div>
					<CBusinessCardUp
						:ref="el => setBusinessCardRef(el)"
						class="business-card-wrap"
						@onUpload="uploadBusinessCard($event, item)"
					/>
				</div>
			</template>

			<!-- 自定义字段 -->
			<template v-if="item.fieldType === 1">
				<CustomField :key="item.uuid" :field="item" @update="handlePersonalFieldUpdate($event, item)" />
			</template>
		</template>
	</div>
</template>

<script setup>
import { ref, onActivated, onUnmounted, computed, getCurrentInstance, watch } from 'vue';
import { EN } from '@/assets/js/i18n';
import { useRegionSelector } from '@/view/areaCode/useRegionSelector';
import CustomField from './index.vue';
import LabelField from '../LabelField.vue';
import cSearchPicker from '@/view/investor_appointment/components/c-search-picker';
import CBusinessCardUp from '@rebuild/components/form/CBusinessCardUp.vue';
import { Field as VanField } from 'vant';

const DEFAULT_NUMBER_CODE = '86';

const { proxy } = getCurrentInstance();

const props = defineProps({
	fields: {
		type: Array,
		required: true
	},
	sendVerifyCode: {
		type: Function,
		default: null
	}
});

const emit = defineEmits(['onUpdateAreaCode', 'onUpdateBusinessCardUpload', 'onUpdateAuthCode']);

// 区号处理
const { buildScopeKey, getRegion, openRegionSelector, clearRegion } = useRegionSelector();
const scopeKey = buildScopeKey('personal_fields_form');
const selectedRegion = computed(() => getRegion(scopeKey));
const innerAreaCode = ref(DEFAULT_NUMBER_CODE);

onActivated(() => {
	if (selectedRegion.value) {
		const { key } = selectedRegion.value;
		onAreaCodeConfirm(key);
	}
});
onUnmounted(() => {
	clearRegion(scopeKey);
});

const onAreaCodeConfirm = areaKey => {
	if (areaKey) {
		const areaCodeFromKey = areaKey.split('_')[1];
		if (areaCodeFromKey) {
			innerAreaCode.value = areaCodeFromKey.slice(1);
		}
	}
	emit('onUpdateAreaCode', innerAreaCode.value);
};
const openAreaCodePicker = () => {
	openRegionSelector(scopeKey, {
		areaCode: innerAreaCode.value ? `+${innerAreaCode.value}` : null,
		local: '1'
	});
};

const verificationPlaceholder = computed(() => {
	if (proxy.$i18n.locale === EN) {
		return 'Please enter';
	}
	return proxy.$t('enterprise.normal-field.tips', { label: proxy.$t('enterprise.normal-field.verification') });
});

// 邮箱+验证码 (systemType 8)
const innerAuthCode = ref('');
const countdown = ref(0);
let verifyCodeTimer = null;
const emitAuthCode = () => {
	emit('onUpdateAuthCode', innerAuthCode.value);
};
const handleSendVerifyCode = async item => {
	if (countdown.value > 0) return;
	const beforeHook = props.sendVerifyCode;
	if (typeof beforeHook !== 'function') return;

	try {
		await beforeHook({ field: item, email: item?.dynamicFieldValue || '' });
		countdown.value = 60;
		verifyCodeTimer = setInterval(() => {
			if (countdown.value > 0) {
				countdown.value--;
			} else if (verifyCodeTimer) {
				clearInterval(verifyCodeTimer);
				verifyCodeTimer = null;
			}
		}, 1000);
	} catch (_) {
		// 错误由使用者处理
	}
};
onUnmounted(() => {
	if (verifyCodeTimer) {
		clearInterval(verifyCodeTimer);
		verifyCodeTimer = null;
	}
});

// 名片处理
const businessCardRef = ref(null);
const setBusinessCardRef = el => {
	if (el) {
		businessCardRef.value = el;
	}
};
const uploadBusinessCard = (result, item) => {
	emit('onUpdateBusinessCardUpload', { result, item });
};
watch(
	() => props.fields,
	newVal => {
		const businessCardField = newVal.find(item => Number(item.systemType) === 6);
		if (businessCardField && businessCardField.dynamicFieldValue) {
			// 名片组件回填
			businessCardRef.value?.setDefaultCard(businessCardField.dynamicFieldValue);
		}
	},
	{ deep: true }
);

const validateNumber = (val, item) => {
	if (!item) return;
	proxy.$set(item, 'dynamicFieldValue', val.replace(/\D/g, ''));
};

const inputCompany = ({ name }, item) => {
	if (!item) return;
	proxy.$set(item, 'dynamicFieldValue', name || '');
};

const handlePersonalFieldUpdate = ({ value }, item) => {
	if (!item) return;
	proxy.$set(item, 'dynamicFieldValue', value || '');
};
</script>

<style lang="less" scoped>
@import '@/style/mixins.less';

.form-box {
	margin-bottom: 0.12rem;
	padding: 0 0.12rem;
	background: #fff;
	.item {
		padding: 0.14rem 0;
		display: flex;
		align-items: center;
		justify-items: center;
		font-size: 0.15rem;
		color: #333;
		&:not(:last-child) {
			border-bottom: 0.5px solid #e8eaef;
		}
		.label {
			flex-shrink: 0;
			width: 0.83rem;
		}
		.item-content {
			flex: 1;
			display: flex;
			justify-content: flex-end;
			align-items: center;
			line-height: 0.21rem;

			.icon-font {
				margin-left: 0.06rem;
			}
		}
		.item-value {
			max-width: 76%;
			color: #333;
			font-family: 'PingFang SC';
			font-size: 0.15rem;
			font-style: normal;
			font-weight: 400;
			text-align: right;
			word-break: break-all;
		}
		.item-placeholder {
			color: #dcdcdc;
		}

		.common-flex {
			flex: 1;
			margin-left: 0.06rem;
			display: flex;
			align-items: center;
			text-align: right;
		}
		.email-auth-label {
			width: 1rem;
		}
		.email-auth-input {
			width: 1.2rem;
		}
		.item-suffix {
			flex-shrink: 0;
			margin-left: 0.12rem;
			cursor: pointer;
			&--disabled {
				opacity: 0.6;
				cursor: not-allowed;
			}
			span {
				color: var(--ir-theme-color);
				font-size: 0.14rem;
				line-height: 0.21rem;
			}
		}
		.label .required.ml6 {
			color: #eb5149;
			font-size: 0.15rem;
			margin-left: 0.04rem;
		}
		.area-input {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			width: 0.9rem;
			font-size: 0;

			.area-code {
				color: #333;
				font-family: 'PingFang SC';
				font-size: 0.14rem;
				font-style: normal;
				font-weight: 400;
				line-height: 0.18rem;
			}
			.drop-popup {
				margin-left: 0.08rem;
				font-size: 0.1rem;
				color: #c5c5c5;
			}
			.area-separator {
				margin-left: 0.18rem;
				margin-right: 0.09rem;
				width: 0.01rem;
				height: 0.18rem;
				background: #e8eaef;
			}
		}
		.common-input {
			flex: 1;
			margin-left: 0.06rem;
			border: none;
			text-align: right;
			height: 0.21rem;
			line-height: 0.21rem;
		}
		.tip {
			font-size: 0.13rem;
			color: #999999;
			letter-spacing: 0.26px;
			line-height: 0.18rem;
		}
		.commitments {
			font-size: 0.13rem;
			color: var(--ir-theme-color);
			padding-left: 0.06rem;
			word-break: break-all;
		}
		.icon {
			margin-right: 0.02rem;
			font-size: 0.14rem;
			color: #989898;
		}
		.agree {
			color: var(--ir-theme-color);
		}
		.common-select-wrap {
			display: inline-block;
			margin-right: 0.01rem;
			vertical-align: -0.02rem;
		}
		.common-select {
			font-size: 0.18rem;

			&.common-selected {
				color: var(--ir-theme-color);
			}
		}
	}
	.upload-box {
		position: relative;
		padding: 0.16rem 0;
		display: flex;
		align-items: flex-start;
		font-family: 'PingFang SC';
		.upload-title {
			color: #333;
			font-size: 0.16rem;
			font-weight: 500;
			line-height: 140%;
		}
		.upload-tip {
			padding-top: 0.07rem;
			color: #989898;
			font-size: 0.12rem;
			font-weight: 400;
			line-height: 140%;
		}
		.business-card-wrap {
			margin-left: 0.14rem;
		}
	}
}

.c-search {
	/deep/.picker-list {
		top: 186% !important;
	}
	/deep/.text-area {
		min-height: 0.21rem;
		line-height: 0.21rem;
	}
}

/deep/ .search-picker {
	.text-area::placeholder {
		color: #dcdcdc;
	}
}

.item.item--textarea {
	display: flex;
	flex-direction: column;
	align-items: flex-start;

	/deep/ .item-content--textarea {
		width: 100%;
		padding-top: 0.12rem;
		padding-bottom: 0.12rem;

		.van-cell {
			padding: 0.12rem;
			.thinner-border-around(#e8eaef, 0.04rem);

			&:before {
				pointer-events: none;
				z-index: 1;
			}
		}
		.van-field__control {
			min-height: 1.5rem;
			color: #1d2129;
			font-family: 'PingFang SC';
			font-size: 0.15rem;
			font-style: normal;
			font-weight: 400;
			line-height: 0.21rem;

			&::placeholder,
			&::-webkit-input-placeholder {
				color: #c5c5c5;
			}
		}
	}
}

.clear-van-field {
	flex: 1;
	width: 0;
	border: none;
	height: 0.21rem;
	padding: 0;

	/deep/ .van-field__control {
		text-align: right;
		font-size: 0.15rem;
		color: #333;
		line-height: 0.21rem;

		&::placeholder,
		&::-webkit-input-placeholder {
			color: #dcdcdc;
		}
	}
}
</style>
